/**
 * Unified AUI Types - Transcription Types
 *
 * Types for media transcription versioning system:
 * - Transcription types (audio, ocr, caption, description, manual)
 * - Transcription versions with model provenance
 * - Transcription segments with timestamps
 * - Transcription jobs for async processing
 *
 * @module @humanizer/core/aui/types/transcription-types
 */
import { z } from 'zod';
// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Deterministic UUID used as archive_id for local (Electron) transcriptions.
 * The aui_transcription_versions table requires a UUID NOT NULL for archive_id,
 * so we use this well-known value instead of the string 'local'.
 */
export const LOCAL_ARCHIVE_UUID = '00000000-0000-0000-0000-000000000001';
// ═══════════════════════════════════════════════════════════════════════════
// ZOD SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════
/** Transcription type enum schema */
export const TranscriptionTypeSchema = z.enum([
    'audio',
    'ocr',
    'caption',
    'description',
    'manual',
]);
/** Transcription status enum schema */
export const TranscriptionStatusSchema = z.enum([
    'pending',
    'processing',
    'completed',
    'failed',
    'cancelled',
]);
/** Transcription provider enum schema */
export const TranscriptionProviderSchema = z.enum([
    'ollama',
    'openai',
    'anthropic',
    'google',
    'cloudflare',
    'local',
]);
/** Transcription priority enum schema */
export const TranscriptionPrioritySchema = z.enum(['low', 'normal', 'high']);
/** Word schema */
export const TranscriptionWordSchema = z.object({
    word: z.string(),
    start: z.number(),
    end: z.number(),
    confidence: z.number().optional(),
});
/** Segment schema */
export const TranscriptionSegmentSchema = z.object({
    id: z.number(),
    start: z.number(),
    end: z.number(),
    text: z.string(),
    confidence: z.number().optional(),
    speaker: z.string().optional(),
    words: z.array(TranscriptionWordSchema).optional(),
});
/** Model info schema */
export const TranscriptionModelInfoSchema = z.object({
    id: z.string(),
    provider: TranscriptionProviderSchema,
    version: z.string().optional(),
    variant: z.string().optional(),
});
/** Quality metrics schema */
export const TranscriptionQualityMetricsSchema = z.object({
    confidence: z.number().optional(),
    wordCount: z.number().optional(),
    segmentCount: z.number().optional(),
    language: z.string().optional(),
});
/** Create transcription request schema */
export const CreateTranscriptionRequestSchema = z.object({
    mediaId: z.string(),
    archiveId: z.string(),
    type: TranscriptionTypeSchema,
    modelId: z.string().optional(),
    priority: TranscriptionPrioritySchema.optional(),
    requestedBy: z.string().optional(),
});
/** List transcriptions options schema */
export const ListTranscriptionsOptionsSchema = z.object({
    mediaId: z.string().optional(),
    archiveId: z.string().optional(),
    type: TranscriptionTypeSchema.optional(),
    status: TranscriptionStatusSchema.optional(),
    preferredOnly: z.boolean().optional(),
    limit: z.number().optional(),
    offset: z.number().optional(),
});
//# sourceMappingURL=transcription-types.js.map